import React, { useEffect, useState } from "react";
import UserSidebar from "./UserSidebar";
import { Outlet } from "react-router-dom";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const DashBoard = () => {
  const [userDomains, setUserDomains] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [userWatchlist, setUserWatchlist] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchUserData() {
      const host = import.meta.env.VITE_HOST;
      const port = import.meta.env.VITE_PORT;
      const token = localStorage.getItem("jwt_token");
      const userId = localStorage.getItem("user_id");

      if (!token || !userId) {
        setError("Token o user ID mancante. Effettua il login.");
        return;
      }

      try {
        // Fetch domini acquistati
        const domainRes = await fetch(
          `${host}:${port}/api/v1/users/get_user_domain?_id=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const domainJson = await domainRes.json();
        if (domainRes.ok) {
          setUserDomains(domainJson.data || []);
          setError("");
        } else {
          setError(domainJson.detail || domainJson.message || "Errore durante il recupero dei domini.");
        }

        // Fetch ordini
        const ordersRes = await fetch(
          `${host}:${port}/api/v1/users/get_user_orders?_id=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const ordersJson = await ordersRes.json();
        if (ordersRes.ok) {
          const sortedOrders = (ordersJson.data || []).sort(
            (a, b) => new Date(a.order_date) - new Date(b.order_date)
          );
          setUserOrders(sortedOrders);
        } else {
          setError(ordersJson.detail || ordersJson.message || "Errore durante il recupero degli ordini.");
        }

        // Fetch watchlist
        const watchlistRes = await fetch(
          `${host}:${port}/api/v1/users/get_user_watchlist`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const watchlistJson = await watchlistRes.json();
        if (watchlistRes.ok) {
          setUserWatchlist(watchlistJson.data || []);
        } else {
          setError(watchlistJson.detail || watchlistJson.message || "Errore durante il recupero della watchlist.");
        }
      } catch (err) {
        setError("Errore nella richiesta al server.");
      }
    }

    fetchUserData();
  }, []);

  // Prepara i dati per il grafico (converte date)
  const chartData = userOrders.map((order) => ({
    date: new Date(order.order_date).toLocaleDateString(),
    amount: Number(order.amount),
  }));

  return (
    <div className="flex h-[calc(100vh-40px)] bg-white">
      <UserSidebar />
      <main
        className="flex-1 px-4 py-5 overflow-y-scroll"
        style={{
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge
        }}
      >
        <h1 className="text-3xl font-bold mb-6" style={{ color: "#94c41c" }}>
          Benvenuto nella tua Dashboard
        </h1>

        {/* Domini acquistati */}
        <div className="bg-white shadow-md p-6 mb-6 border h-[30%] overflow-y-auto">
          <h2 className="text-xl font-semibold mb-4" style={{ color: "#94c41c" }}>
            Domini Acquistati
          </h2>
          {error && <p className="text-red-500">{error}</p>}
          {!error && userDomains.length === 0 && (
            <p className="text-gray-700">Nessun dominio acquistato.</p>
          )}
          {userDomains.length > 0 && (
            <table className="w-full table-auto border-collapse text-left border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-gray-900 font-semibold">
                    Dominio
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-gray-900 font-semibold">
                    Categoria
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-gray-900 font-semibold">
                    Prezzo (€)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-gray-900 font-semibold">
                    Comprato il
                  </th>
                </tr>
              </thead>
              <tbody>
                {userDomains.map((domain) => (
                  <tr key={domain.domain_id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 text-black">
                      {domain.domain_name}
                      {domain.domain_extension}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-black">
                      {domain.category_name || "N/A"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-black">
                      {domain.domain_price?.toFixed(2) || "0.00"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-black">
                      {new Date(domain.creation_date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Grafico pagamenti + Watchlist */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(70%-1.5rem)]">
          {/* Storico pagamenti */}
          <div className="bg-white shadow-md p-6 border flex flex-col h-full">
            <h2 className="text-xl font-semibold mb-4" style={{ color: "#94c41c" }}>
              Storico Pagamenti
            </h2>
            {userOrders.length === 0 ? (
              <p className="text-gray-700 flex-grow flex items-center justify-center">
                Nessun ordine trovato.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis
                    tickFormatter={(value) => `€${value.toFixed(2)}`}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#94c41c"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Watchlist */}
          <div className="bg-white shadow-md p-6 border flex flex-col h-full overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4" style={{ color: "#94c41c" }}>
              Watchlist Domini
            </h2>
            {userWatchlist.length === 0 ? (
              <p className="text-gray-700 flex-grow flex items-center justify-center">
                Nessun dominio nella watchlist.
              </p>
            ) : (
              <ul className="space-y-2 overflow-y-auto">
                {userWatchlist.map((item) => (
                  <li
                    key={item.id}
                    className="bg-gray-100 p-3 rounded shadow text-black flex justify-between items-center"
                  >
                    <div>
                      {item.domain_name}
                      {item.domain_extension}
                    </div>
                    <div className="text-sm text-gray-600">
                      Inserito il: {new Date(item.created_at).toLocaleDateString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <Outlet />
      </main>
    </div>
  );
};

export default DashBoard;
